import { parseHexWithAlpha } from "@/layers/utility/color";
import { EARTHQUAKE_GLYPH_PATH, EARTHQUAKE_GLYPH_VIEWBOX } from "../assets/earthquakeGlyph";

export const POINT_FILL_ALPHA = 1;
export const POINT_ICON_PIXEL_RATIO = 2;
export const POINT_ICON_MIN_SIZED_MAGNITUDE = 1;
export const POINT_ICON_SIZE_AT_MAGNITUDE_ONE = 0.1;
export const POINT_ICON_SIZE_AT_MAGNITUDE_TEN = 1;

const POINT_ICON_CANVAS_SIZE = 256;
const POINT_ICON_FILL_RADIUS_RATIO = 0.41;
const POINT_ICON_OUTER_BORDER_WIDTH_RATIO = 0.05;
const MIN_CLUSTER_RADIUS = 16;

/**
 * Clamps numeric opacity values to the CSS-compatible [0..1] range.
 */
function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Maps earthquake magnitude to the Mapbox icon-size factor used by point symbols.
 * Values below `POINT_ICON_MIN_SIZED_MAGNITUDE` keep the same base size.
 */
function pointIconSizeFactorFromMagnitude(magnitude: number): number {
  const clampedMagnitude = Math.max(POINT_ICON_MIN_SIZED_MAGNITUDE, Math.min(10, magnitude));
  return (
    POINT_ICON_SIZE_AT_MAGNITUDE_ONE +
    ((clampedMagnitude - POINT_ICON_MIN_SIZED_MAGNITUDE) / (10 - POINT_ICON_MIN_SIZED_MAGNITUDE)) *
      (POINT_ICON_SIZE_AT_MAGNITUDE_TEN - POINT_ICON_SIZE_AT_MAGNITUDE_ONE)
  );
}

/**
 * Computes rendered point icon outer radius (including stroke) for a given magnitude.
 */
function pointIconOuterRadiusFromMagnitude(magnitude: number): number {
  const baseIconOuterRadius =
    (POINT_ICON_CANVAS_SIZE / POINT_ICON_PIXEL_RATIO) *
    (POINT_ICON_FILL_RADIUS_RATIO + POINT_ICON_OUTER_BORDER_WIDTH_RATIO / 2);
  return baseIconOuterRadius * pointIconSizeFactorFromMagnitude(magnitude);
}

/**
 * Extracts RGB channels from supported color formats.
 */
function parseRgbChannels(color: string): { r: number; g: number; b: number } | null {
  const normalized = parseHexWithAlpha(color).trim();

  const rgbaMatch = normalized.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(",").map((part) => part.trim());
    if (parts.length >= 3) {
      const r = Number(parts[0]);
      const g = Number(parts[1]);
      const b = Number(parts[2]);
      if ([r, g, b].every(Number.isFinite)) {
        return {
          r: Math.round(r),
          g: Math.round(g),
          b: Math.round(b),
        };
      }
    }
    return null;
  }

  const hexMatch = normalized.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (!hexMatch) {
    return null;
  }

  const rgbHex = hexMatch[1];
  return {
    r: Number.parseInt(rgbHex.substring(0, 2), 16),
    g: Number.parseInt(rgbHex.substring(2, 4), 16),
    b: Number.parseInt(rgbHex.substring(4, 6), 16),
  };
}

/**
 * Converts an sRGB channel to linear color space for luminance calculation.
 */
function srgbToLinear(channel: number): number {
  const c = channel / 255;
  if (c <= 0.04045) {
    return c / 12.92;
  }
  return ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * Picks black or white glyph color based on best contrast against the fill color.
 */
export function glyphColorFromFill(color: string): "#000000" | "#FFFFFF" {
  const rgb = parseRgbChannels(color);
  if (!rgb) {
    return "#000000";
  }

  const luminance = 0.2126 * srgbToLinear(rgb.r) + 0.7152 * srgbToLinear(rgb.g) + 0.0722 * srgbToLinear(rgb.b);
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithWhite > contrastWithBlack ? "#FFFFFF" : "#000000";
}

/**
 * Creates a stable short hash used for generated Mapbox image IDs.
 */
export function colorHash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Applies an additional alpha multiplier while preserving the original RGB channels.
 * Supports hex and rgb/rgba color inputs.
 */
export function applyAlphaMultiplier(color: string, alphaMultiplier: number): string {
  const normalized = parseHexWithAlpha(color).trim();

  const rgbaMatch = normalized.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(",").map((part) => part.trim());
    if (parts.length >= 3) {
      const r = Number(parts[0]);
      const g = Number(parts[1]);
      const b = Number(parts[2]);
      const baseAlpha = parts[3] == null ? 1 : Number(parts[3]);

      if ([r, g, b, baseAlpha].every(Number.isFinite)) {
        const alpha = clamp01(baseAlpha * alphaMultiplier);
        return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha.toFixed(2)})`;
      }
    }
    return normalized;
  }

  const hexMatch = normalized.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (hexMatch) {
    const rgbHex = hexMatch[1];
    const alphaHex = hexMatch[2];
    const r = Number.parseInt(rgbHex.substring(0, 2), 16);
    const g = Number.parseInt(rgbHex.substring(2, 4), 16);
    const b = Number.parseInt(rgbHex.substring(4, 6), 16);
    const baseAlpha = alphaHex ? Number.parseInt(alphaHex, 16) / 255 : 1;
    const alpha = clamp01(baseAlpha * alphaMultiplier);
    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
  }

  return normalized;
}

/**
 * Convenience helper for applying the shared earthquake fill alpha.
 */
export function toSemiTransparentFillColor(color: string): string {
  return applyAlphaMultiplier(color, POINT_FILL_ALPHA);
}

/**
 * Renders the unclustered earthquake icon (fill + outer border + black glyph) into an ImageData.
 */
export function buildCompositePointIconImage(color: string): ImageData | null {
  if (typeof document === "undefined" || typeof Path2D === "undefined") {
    return null;
  }

  const size = POINT_ICON_CANVAS_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  const center = size / 2;
  const radius = size * POINT_ICON_FILL_RADIUS_RATIO;
  const outerBorderWidth = size * POINT_ICON_OUTER_BORDER_WIDTH_RATIO;

  context.clearRect(0, 0, size, size);

  // Draw semi-transparent fill with a white outer stroke ring.
  context.beginPath();
  context.arc(center, center, radius, 0, Math.PI * 2);
  context.globalAlpha = POINT_FILL_ALPHA;
  context.fillStyle = color;
  context.fill();
  context.globalAlpha = 1;
  context.strokeStyle = "#FFFFFF";
  context.lineWidth = outerBorderWidth;
  context.stroke();

  const glyph = new Path2D(EARTHQUAKE_GLYPH_PATH);
  const glyphTargetHeight = size * 0.72;
  const glyphScale = glyphTargetHeight / EARTHQUAKE_GLYPH_VIEWBOX.height;
  const glyphWidth = EARTHQUAKE_GLYPH_VIEWBOX.width * glyphScale;
  const glyphHeight = EARTHQUAKE_GLYPH_VIEWBOX.height * glyphScale;
  context.save();
  context.translate(center - glyphWidth / 2, center - glyphHeight / 2);
  context.scale(glyphScale, glyphScale);
  context.fillStyle = glyphColorFromFill(color);
  context.fill(glyph);
  context.restore();

  return context.getImageData(0, 0, size, size);
}

/**
 * Returns pie marker radius for clustered earthquakes based on average clustered magnitude.
 * Falls back to count buckets when magnitude aggregation is missing.
 */
export function clusterRadiusFromMagnitude(pointCount: number, totalMagnitude?: number): number {
  const magnitudeSum = Number(totalMagnitude);
  if (pointCount > 0 && Number.isFinite(magnitudeSum) && magnitudeSum > 0) {
    const averageMagnitude = magnitudeSum / pointCount;
    const averagePointRadius = pointIconOuterRadiusFromMagnitude(averageMagnitude);
    return Math.max(MIN_CLUSTER_RADIUS, averagePointRadius);
  }

  if (pointCount < 10) {
    return 16;
  }
  if (pointCount < 50) {
    return 20;
  }
  if (pointCount < 100) {
    return 26;
  }
  return 32;
}

/**
 * Derives border width that matches the default point icon stroke proportions.
 */
export function borderWidthFromOuterRadius(outerRadius: number): number {
  const denominator = POINT_ICON_FILL_RADIUS_RATIO + POINT_ICON_OUTER_BORDER_WIDTH_RATIO / 2;
  if (denominator <= 0) {
    return 0;
  }
  return outerRadius * (POINT_ICON_OUTER_BORDER_WIDTH_RATIO / denominator);
}

/**
 * Converts polar coordinates into cartesian coordinates for SVG pie path generation.
 */
function polarToCartesian(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}

/**
 * Builds an SVG path for a single pie slice.
 */
export function pieSlicePath(cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}
